/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

function writeUTFBytes(view: DataView, offset: number, value: string): void {
  const length = value.length;

  for (let i = 0; i < length; i++) {
    view.setUint8(offset + i, value.charCodeAt(i));
  }
}

export function arrayToWaveDataView(
  sourceSampleRate: number,
  data: Float32Array
): DataView {
  const buffer = new ArrayBuffer(44 + data.length * 2);
  const view = new DataView(buffer);

  // Wave file format spec: http://soundfile.sapp.org/doc/WaveFormat/
  // === RIFF header ===
  /* ChunkID       */ writeUTFBytes(view, 0, "RIFF");
  /* ChunkSize     */ view.setUint32(4, 36 + data.length * 2, true);
  /* Format        */ writeUTFBytes(view, 8, "WAVE");

  // === "fmt" sub-chunk ===
  /* Subchunk1ID   */ writeUTFBytes(view, 12, "fmt ");
  /* Subchunk1Size */ view.setUint32(16, 16, true);
  /* AudioFormat   */ view.setUint16(20, 1, true);
  /* NumChannels   */ view.setUint16(22, 1, true);
  /* SampleRate    */ view.setUint32(24, sourceSampleRate, true);
  /* ByteRate      */ view.setUint32(28, sourceSampleRate * 2, true);
  /* BlockAlign    */ view.setUint16(32, 2, true);
  /* BitsPerSample */ view.setUint16(34, 16, true);

  // === "data" sub-chunk ===
  /* Subchunk2ID   */ writeUTFBytes(view, 36, "data");
  /* Subchunk2Size */ view.setUint32(40, data.length * 2, true);

  // === Sound data ===
  const length = data.length;
  const volume = 1;
  let index = 44;

  for (let i = 0; i < length; i++) {
    view.setInt16(index, data[i] * (0x7fff * volume), true);
    index += 2;
  }

  return view;
}
